import { FormEvent, useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { BookOpen, Check, ChevronLeft, Eye, EyeOff, Factory } from 'lucide-react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { AliyunCaptchaModal, type AliyunCaptchaResult } from '../components/AliyunCaptchaModal';
import { BrandMark } from '../components/BrandMark';
import { WechatMiniprogramIcon } from '../components/icons/WechatMiniprogramIcon';
import { SiWechat } from '../components/icons/reactIconsSi';
import { QrLoginPanel } from '../components/QrLoginPanel';
import { appConfig } from '../config/appConfig';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/authService';
import {
  buildCaptchaRequest,
  formatErrorMessage,
  isStrongPassword,
  isUserNotRegisteredMessage,
  isPasswordNotSetMessage,
  isValidChinaPhone,
  normalizePhoneNumber,
  resolveCooldownSeconds,
  type AuthCaptchaScene,
} from '../utils/authFormUtils';
import { isOnboardingDone, markOnboardingDone } from '../utils/onboardingSession';
import {
  clearFactoryLoginReturn,
  consumePreferFactoryLogin,
  consumeWechatFactoryReturn,
  isApprovedFactoryWorker,
  markPreferFactoryLogin,
  markWechatFactoryReturn,
  peekPreferFactoryLogin,
  peekWechatFactoryReturn,
} from '../lib/factoryContext';

type AuthScreen = 'signin' | 'signup' | 'forgot-password';
type CaptchaScene = AuthCaptchaScene;
type PendingCaptchaRequest = { scene: CaptchaScene; phone: string };

function useCountdown(value: number, setValue: Dispatch<SetStateAction<number>>) {
  useEffect(() => {
    if (value <= 0) {
      return;
    }

    const timer = window.setInterval(() => {
      setValue((current) => (current <= 1 ? 0 : current - 1));
    }, 1000);

    return () => window.clearInterval(timer);
  }, [setValue, value]);
}

function FeedbackBanner({ message, tone }: { message: string; tone: 'error' | 'success' }) {
  return (
    <p
      className={`rounded-xl px-4 py-3 text-sm ${
        tone === 'error'
          ? 'border border-[#F2C6CC] bg-[#FFF3F3] text-danger'
          : 'border border-[#CDE8D0] bg-[#F1FAF1] text-success'
      }`}
    >
      {message}
    </p>
  );
}

export function LoginPage() {
  const { status, user, signInWithCode, signInWithPassword, error, clearError } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const intentFactory = searchParams.get('intent') === 'factory';
  const intentLearningShare = searchParams.get('intent') === 'learning-share';
  const from = intentFactory
    ? '/factory/orders'
    : intentLearningShare ? '/join/continue' : (location.state as { from?: string } | null)?.from || '/';
  const [factoryCapDecision, setFactoryCapDecision] = useState<'unknown' | 'approved' | 'denied'>('unknown');

  useEffect(() => {
    if (intentFactory) consumePreferFactoryLogin();
  }, [intentFactory]);

  useEffect(() => {
    if (status !== 'signed-in' || !intentFactory) {
      setFactoryCapDecision('unknown');
      return undefined;
    }
    if (isApprovedFactoryWorker(user)) {
      setFactoryCapDecision('approved');
      return undefined;
    }

    let cancelled = false;
    void (async () => {
      try {
        const fresh = await authService.fetchSession();
        if (cancelled) return;
        if (isApprovedFactoryWorker(fresh)) {
          setFactoryCapDecision('approved');
          return;
        }
        const workerStatus = fresh.capabilities?.factoryWorker;
        if (typeof workerStatus !== 'string' || workerStatus === 'approved') return;
        if (!cancelled) setFactoryCapDecision('denied');
      } catch {
        // stay on factory gate until /auth/me confirms capability
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [intentFactory, status, user?.capabilities?.factoryWorker, user?.id]);

  const [screen, setScreen] = useState<AuthScreen>(intentLearningShare && searchParams.get('mode') === 'signup' ? 'signup' : 'signin');
  const [mode, setMode] = useState<'password' | 'code'>('password');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [signupPhone, setSignupPhone] = useState('');
  const [signupAuthCode, setSignupAuthCode] = useState('');
  const [forgotPhone, setForgotPhone] = useState('');
  const [forgotAuthCode, setForgotAuthCode] = useState('');
  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [codeCooldown, setCodeCooldown] = useState(0);
  const [signupSubmitting, setSignupSubmitting] = useState(false);
  const [signupSendingCode, setSignupSendingCode] = useState(false);
  const [signupCodeCooldown, setSignupCodeCooldown] = useState(0);
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotSendingCode, setForgotSendingCode] = useState(false);
  const [forgotCodeCooldown, setForgotCodeCooldown] = useState(0);
  const [sendCodeMessage, setSendCodeMessage] = useState<string | null>(null);
  const [signupMessage, setSignupMessage] = useState<string | null>(null);
  const [forgotMessage, setForgotMessage] = useState<string | null>(null);
  const [captchaVisible, setCaptchaVisible] = useState(false);
  const [pendingCaptchaRequest, setPendingCaptchaRequest] = useState<PendingCaptchaRequest | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [agreedToPolicy, setAgreedToPolicy] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showForgotNewPassword, setShowForgotNewPassword] = useState(false);
  const [showForgotConfirmPassword, setShowForgotConfirmPassword] = useState(false);
  const [wechatQrOpen, setWechatQrOpen] = useState(false);
  const [wechatHint, setWechatHint] = useState<string | null>(null);
  const [wechatStarting, setWechatStarting] = useState(false);

  useCountdown(codeCooldown, setCodeCooldown);
  useCountdown(signupCodeCooldown, setSignupCodeCooldown);
  useCountdown(forgotCodeCooldown, setForgotCodeCooldown);

  const effectiveError = localError || (screen === 'signin' ? error : null);
  const normalizedForgotNewPassword = forgotNewPassword.trim();
  const normalizedForgotConfirmPassword = forgotConfirmPassword.trim();
  const forgotPasswordRuleHint = normalizedForgotNewPassword && !isStrongPassword(normalizedForgotNewPassword)
    ? '新密码至少 8 位，且包含字母、数字和符号'
    : undefined;
  const forgotPasswordMismatchHint = normalizedForgotConfirmPassword
    && normalizedForgotNewPassword
    && normalizedForgotConfirmPassword !== normalizedForgotNewPassword
    ? '两次输入的新密码不一致'
    : undefined;

  const resetFeedback = useCallback(() => {
    clearError();
    setLocalError(null);
    setWechatHint(null);
  }, [clearError]);

  const navigateToSignIn = useCallback((options?: { phone?: string; mode?: 'password' | 'code'; message?: string }) => {
    resetFeedback();
    setScreen('signin');
    setPendingCaptchaRequest(null);
    setSignupMessage(null);
    setForgotMessage(null);
    setCaptchaVisible(false);
    setWechatQrOpen(false);
    setSendCodeMessage(options?.message || null);
    if (typeof options?.phone === 'string') {
      setPhone(options.phone);
    }
    if (options?.mode) {
      setMode(options.mode);
    }
  }, [resetFeedback]);

  const navigateToSignup = useCallback(() => {
    resetFeedback();
    setSendCodeMessage(null);
    setSignupMessage(null);
    setForgotMessage(null);
    setPendingCaptchaRequest(null);
    setCaptchaVisible(false);
    setSignupPhone((current) => current || normalizePhoneNumber(phone));
    setSignupAuthCode('');
    setAgreedToPolicy(false);
    setWechatQrOpen(false);
    setScreen('signup');
  }, [phone, resetFeedback]);

  const navigateToForgotPassword = useCallback(() => {
    resetFeedback();
    setSendCodeMessage(null);
    setSignupMessage(null);
    setForgotMessage(null);
    setPendingCaptchaRequest(null);
    setCaptchaVisible(false);
    setForgotPhone((current) => current || normalizePhoneNumber(phone));
    setForgotAuthCode('');
    setScreen('forgot-password');
  }, [phone, resetFeedback]);

  const handleCaptchaSuccess = useCallback(async (result: AliyunCaptchaResult) => {
    const request = pendingCaptchaRequest;
    setCaptchaVisible(false);

    if (!request) {
      return;
    }

    resetFeedback();

    try {
      const payload = buildCaptchaRequest(result, request.phone, request.scene);
      if (request.scene === 'signup') {
        setSignupSendingCode(true);
        const response = await authService.verifyCaptchaAndRequestAuthCode(payload);
        setSignupMessage(response.message);
        setSignupCodeCooldown(resolveCooldownSeconds(response.expiresAt));
      } else if (request.scene === 'signin') {
        setSendingCode(true);
        const response = await authService.verifyCaptchaAndRequestAuthCode(payload);
        setSendCodeMessage(response.message);
        setCodeCooldown(resolveCooldownSeconds(response.expiresAt));
      } else {
        setForgotSendingCode(true);
        const response = await authService.verifyCaptchaAndRequestAuthCode(payload);
        setForgotMessage(response.message);
        setForgotCodeCooldown(resolveCooldownSeconds(response.expiresAt));
      }
    } catch (captchaError) {
      setLocalError(formatErrorMessage(captchaError, '发送验证码失败'));
    } finally {
      setSendingCode(false);
      setSignupSendingCode(false);
      setForgotSendingCode(false);
      setPendingCaptchaRequest(null);
    }
  }, [pendingCaptchaRequest, resetFeedback]);

  if (status === 'booting') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#F8FAFD] px-6">
        <BrandMark size={72} />
        <div className="text-center">
          <div className="text-xl font-extrabold text-ink">{appConfig.appName}</div>
          <div className="mt-1 text-sm text-muted">加载中…</div>
        </div>
      </div>
    );
  }

  // Exit-from-factory / WeChat factory return may race to plain /login.
  if (!intentFactory && !intentLearningShare && (peekPreferFactoryLogin() || peekWechatFactoryReturn())) {
    return <Navigate to="/login?intent=factory" replace />;
  }

  const factoryWorkerApproved = isApprovedFactoryWorker(user) || factoryCapDecision === 'approved';
  const factorySignedInNonWorker =
    status === 'signed-in' && intentFactory && factoryCapDecision === 'denied';

  if (status === 'signed-in' && !intentFactory) {
    return <Navigate to={from} replace />;
  }

  if (status === 'signed-in' && intentFactory && factoryWorkerApproved) {
    return <Navigate to="/factory/orders" replace />;
  }

  if (!isOnboardingDone() && !intentFactory && !intentLearningShare) {
    return <Navigate to="/welcome" replace state={{ from }} />;
  }

  const headerTitle = screen === 'signin' ? (intentFactory ? '工厂入口' : '登录') : screen === 'signup' ? '注册账户' : '忘记密码';
  const headerSubtitle = screen === 'signin'
    ? (intentFactory
      ? '使用颗粒日记账户登录工厂产线（需已批准的 FactoryWorker 能力）'
      : '登录以开启颗粒日记')
    : screen === 'signup'
      ? '首次注册前，需要先完成人机图形验证。'
      : '通过图形验证和短信验证码，重新设置登录密码。';

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    resetFeedback();

    const normalizedPhone = normalizePhoneNumber(phone);
    if (!isValidChinaPhone(normalizedPhone)) {
      setLocalError('请输入有效的手机号');
      return;
    }

    if (mode === 'password' && !password.trim()) {
      setLocalError('请输入密码');
      return;
    }

    if (mode === 'code' && !authCode.trim()) {
      setLocalError('请输入验证码');
      return;
    }

    try {
      setSubmitting(true);
      if (mode === 'password') {
        await signInWithPassword(normalizedPhone, password);
      } else {
        await signInWithCode(normalizedPhone, authCode.trim());
      }
    } catch (submitError) {
      const message = submitError instanceof Error ? submitError.message : '登录失败';
      if (isUserNotRegisteredMessage(message)) {
        setSignupPhone(normalizedPhone);
        setScreen('signup');
        setLocalError('该手机号尚未注册。账号注销后需要重新注册，请完成下方注册流程。');
        return;
      }
      if (mode === 'password' && isPasswordNotSetMessage(message)) {
        setLocalError('您尚未设定密码。请使用验证码登录，或通过「忘记密码」设定登录密码。');
        return;
      }
      setLocalError(message);
    } finally {
      setSubmitting(false);
    }
  }

  function handleSendCode() {
    resetFeedback();
    setSendCodeMessage(null);

    const normalizedPhone = normalizePhoneNumber(phone);
    if (!isValidChinaPhone(normalizedPhone)) {
      setLocalError('请先输入有效手机号');
      return;
    }

    setPendingCaptchaRequest({ scene: 'signin', phone: normalizedPhone });
    setCaptchaVisible(true);
  }

  async function handleSignupSendCode() {
    resetFeedback();
    setSignupMessage(null);

    const normalizedPhone = normalizePhoneNumber(signupPhone);
    if (!isValidChinaPhone(normalizedPhone)) {
      setLocalError('请输入有效的手机号');
      return;
    }

    try {
      const exists = await authService.doesPhoneNumberExist(normalizedPhone);
      if (exists) {
        setLocalError('手机号已注册，请直接登录');
        return;
      }

      setPendingCaptchaRequest({ scene: 'signup', phone: normalizedPhone });
      setCaptchaVisible(true);
    } catch (requestError) {
      setLocalError(formatErrorMessage(requestError, '校验手机号失败'));
    }
  }

  async function handleForgotSendCode() {
    resetFeedback();
    setForgotMessage(null);

    const normalizedPhone = normalizePhoneNumber(forgotPhone);
    if (!isValidChinaPhone(normalizedPhone)) {
      setLocalError('请输入有效的手机号');
      return;
    }

    try {
      const exists = await authService.doesPhoneNumberExist(normalizedPhone);
      if (!exists) {
        setLocalError('手机号未注册，请先注册');
        return;
      }

      setPendingCaptchaRequest({ scene: 'password-change', phone: normalizedPhone });
      setCaptchaVisible(true);
    } catch (requestError) {
      setLocalError(formatErrorMessage(requestError, '校验手机号失败'));
    }
  }

  async function handleSignupSubmit(event: FormEvent) {
    event.preventDefault();
    resetFeedback();

    if (!agreedToPolicy) {
      setLocalError('请先阅读并勾选同意《隐私政策》');
      return;
    }

    const normalizedPhone = normalizePhoneNumber(signupPhone);
    if (!isValidChinaPhone(normalizedPhone)) {
      setLocalError('请输入有效的手机号');
      return;
    }

    if (signupAuthCode.trim().length !== 6) {
      setLocalError('请输入 6 位短信验证码');
      return;
    }

    let registered = false;
    try {
      setSignupSubmitting(true);
      await authService.completeSignup(normalizedPhone, signupAuthCode.trim());
      registered = true;
      if (intentLearningShare) {
        navigate('/join/continue', { replace: true, state: { registered: true } });
        return;
      }
      await signInWithCode(normalizedPhone, signupAuthCode.trim());
    } catch (submitError) {
      if (registered) {
        navigateToSignIn({
          phone: normalizedPhone,
          mode: 'code',
          message: '账户已创建！原验证码已失效，请重新完成图形验证后获取新短信验证码登录。',
        });
        return;
      }

      setLocalError(formatErrorMessage(submitError, '注册失败'));
    } finally {
      setSignupSubmitting(false);
    }
  }

  async function handleForgotPasswordSubmit(event: FormEvent) {
    event.preventDefault();
    resetFeedback();

    const normalizedPhone = normalizePhoneNumber(forgotPhone);
    if (!isValidChinaPhone(normalizedPhone)) {
      setLocalError('请输入有效的手机号');
      return;
    }

    if (forgotAuthCode.trim().length !== 6) {
      setLocalError('请输入 6 位短信验证码');
      return;
    }

    if (!normalizedForgotNewPassword) {
      setLocalError('请输入新密码');
      return;
    }

    if (!isStrongPassword(normalizedForgotNewPassword)) {
      return;
    }

    if (!normalizedForgotConfirmPassword) {
      setLocalError('请再次输入新密码');
      return;
    }

    if (normalizedForgotNewPassword !== normalizedForgotConfirmPassword) {
      return;
    }

    try {
      setForgotSubmitting(true);
      const { token } = await authService.verifyPasswordChangeCode(normalizedPhone, forgotAuthCode.trim());
      await authService.confirmPasswordChange(
        normalizedPhone,
        forgotAuthCode.trim(),
        token,
        normalizedForgotNewPassword,
      );
      setForgotPhone('');
      setForgotAuthCode('');
      setForgotNewPassword('');
      setForgotConfirmPassword('');
      setForgotMessage(null);
      navigateToSignIn({
        phone: normalizedPhone,
        mode: 'password',
        message: '密码已更新，请使用新密码登录。',
      });
    } catch (submitError) {
      setLocalError(formatErrorMessage(submitError, '密码重置失败'));
    } finally {
      setForgotSubmitting(false);
    }
  }

  const isFactoryGate = intentFactory && screen === 'signin';

  function startWechatWebsiteLogin() {
    resetFeedback();
    setWechatStarting(true);
    if (isFactoryGate) {
      markOnboardingDone();
      markWechatFactoryReturn();
      markPreferFactoryLogin();
      const returnTo = `${window.location.origin.replace(/\/+$/, '')}/login/oauth/wechat?intent=factory`;
      window.location.assign(authService.buildWechatLoginUrl(returnTo));
      return;
    }
    consumeWechatFactoryReturn();
    const returnTo = intentLearningShare ? `${window.location.origin}/login/oauth/wechat?intent=learning-share` : undefined;
    window.location.assign(authService.buildWechatLoginUrl(returnTo));
  }

  const wechatLoginIcons = (
    <div className="flex items-center justify-center gap-4 py-1">
      <button
        type="button"
        aria-label={screen === 'signup' ? '微信扫码注册' : '微信登录'}
        title={screen === 'signup' ? '微信扫码注册' : '微信登录'}
        onClick={() => startWechatWebsiteLogin()}
        disabled={wechatStarting}
        className="flex h-12 w-12 items-center justify-center rounded-full bg-[#07C160] text-white transition hover:opacity-90 disabled:opacity-60"
      >
        <SiWechat size={22} title="" />
      </button>
      <button
        type="button"
        aria-label={screen === 'signup' ? '微信小程序扫码注册' : '微信小程序扫码登录'}
        title={screen === 'signup' ? '微信小程序扫码注册' : '微信小程序扫码登录'}
        onClick={() => {
          resetFeedback();
          setWechatQrOpen(true);
        }}
        className="flex h-12 w-12 items-center justify-center rounded-full bg-[#6467F0] text-white transition hover:opacity-90"
      >
        <WechatMiniprogramIcon size={28} />
      </button>
    </div>
  );

  return (
    <div
      className={
        isFactoryGate
          ? 'relative min-h-screen bg-[#15191f] text-[#E8ECF1]'
          : 'relative min-h-screen bg-[#F8FAFD]'
      }
    >
      {isFactoryGate ? (
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'linear-gradient(135deg, #fff 1px, transparent 1px), linear-gradient(45deg, #fff 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
          aria-hidden
        />
      ) : null}

      <Link
        to={isFactoryGate ? '/login' : '/welcome'}
        onClick={isFactoryGate ? clearFactoryLoginReturn : undefined}
        className={
          isFactoryGate
            ? 'absolute left-4 top-6 z-10 inline-flex h-10 items-center gap-2 rounded-md border border-[#3a4450] bg-[#1e242c] px-3 text-sm text-[#B8C0CC] transition hover:border-[#2EC4B6]/60 hover:text-[#2EC4B6] sm:left-6'
            : 'absolute left-4 top-6 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full text-primary transition hover:bg-primary/10 sm:left-6'
        }
        aria-label={isFactoryGate ? '返回日记登录' : '返回'}
      >
        {isFactoryGate ? (
          <>
            <BookOpen size={16} aria-hidden />
            <span>日记登录</span>
          </>
        ) : (
          <ChevronLeft size={22} aria-hidden />
        )}
      </Link>

      <div
        className={
          isFactoryGate
            ? 'relative mx-auto flex min-h-screen w-full max-w-[520px] flex-col justify-center px-4 py-12 sm:px-6'
            : 'mx-auto flex min-h-screen w-full max-w-[560px] flex-col justify-center px-4 py-12 sm:px-6'
        }
      >
        <div className={`mb-8 ${isFactoryGate ? '' : 'flex items-start gap-4'}`}>
          {isFactoryGate ? (
            <div className="min-w-0">
              <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
                <BrandMark size={40} tint="#2EC4B6" alt="颗粒工厂" className="shrink-0" />
                <p className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#2EC4B6]">
                  Staff Channel · 产线入口
                </p>
                <span className="hidden h-4 w-px shrink-0 bg-[#3a4450] sm:block" aria-hidden />
                <h1 className="shrink-0 text-3xl font-semibold tracking-tight text-[#F2F5F8]">
                  颗粒工厂
                </h1>
              </div>
              <p className="mt-3 max-w-md text-sm leading-6 text-[#9AA3B0]">
                工作人员用日记账户登录产线。需已批准的 FactoryWorker 能力。学习日记请走左侧「日记登录」。
              </p>
            </div>
          ) : (
            <>
              <BrandMark size={72} className="shrink-0" />
              <div className="min-w-0 pt-1">
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-primary">{appConfig.appName}</p>
                <h1 className="mt-1 text-3xl font-bold tracking-[-0.03em] text-ink">{headerTitle}</h1>
                <p className="mt-2 text-base leading-6 text-muted">{headerSubtitle}</p>
              </div>
            </>
          )}
        </div>

        {screen === 'signin' ? (
          <section
            className={
              isFactoryGate
                ? 'relative rounded-md border border-[#3a4450] bg-[#1e242c] p-6 shadow-[0_12px_40px_rgba(0,0,0,0.35)]'
                : 'relative rounded-2xl border border-border bg-surface p-6 shadow-card'
            }
          >
            {!intentFactory ? (
              <Link
                to="/login?intent=factory"
                aria-label="颗粒工厂员工登录"
                title="颗粒工厂员工登录"
                className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs text-muted/60 transition hover:bg-canvas hover:text-muted"
              >
                <span>颗粒工厂员工登录</span>
                <Factory size={16} aria-hidden className="shrink-0" />
              </Link>
            ) : null}

            {isFactoryGate ? (
              <div className="mb-5 border-b border-[#3a4450] pb-4">
                <div className="inline-flex items-center gap-2 rounded-sm bg-[#2EC4B6]/15 px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#2EC4B6]">
                  非学习壳
                </div>
                <h2 className="mt-3 text-base font-semibold text-[#F2F5F8]">
                  {factorySignedInNonWorker
                    ? '无法进入产线'
                    : wechatQrOpen
                      ? '微信小程序扫码登录'
                      : '产线身份验证'}
                </h2>
                <p className="mt-1 text-sm text-[#9AA3B0]">
                  {factorySignedInNonWorker
                    ? '当前账户不是已批准的产线工人，无法授权产线登录。'
                    : wechatQrOpen
                      ? '打开颗粒日记小程序扫一扫，确认后即可登录产线。'
                      : '密码、短信验证码或微信登录进入工厂五域工作台。'}
                </p>
              </div>
            ) : (
              <div className="mb-5">
                <h2 className="text-base font-semibold text-ink">
                  {wechatQrOpen ? '微信小程序扫码登录' : '账户验证'}
                </h2>
                <p className="mt-1 text-sm text-muted">
                  {wechatQrOpen
                    ? '打开颗粒日记小程序扫一扫，确认后即可登录网页版。'
                    : '使用密码或短信验证码继续。'}
                </p>
              </div>
            )}

            {factorySignedInNonWorker ? (
              <div className="space-y-4">
                <p className="text-lg font-semibold text-[#2EC4B6]">无法进入产线</p>
                <p className="text-sm leading-6 text-[#C5CDD8]">
                  当前账户不是工厂员工。若要使用学习壳，请进入颗粒日记。
                </p>
                <button
                  type="button"
                  onClick={() => {
                    clearFactoryLoginReturn();
                    markOnboardingDone();
                    navigate('/', { replace: true });
                  }}
                  className="w-full rounded-md bg-[#2EC4B6] px-4 py-2.5 text-sm font-semibold text-[#15191f]"
                >
                  进入颗粒日记
                </button>
              </div>
            ) : wechatQrOpen ? (
              <div className="space-y-4">
                <button
                  type="button"
                  onClick={() => {
                    setWechatQrOpen(false);
                    resetFeedback();
                  }}
                  className={
                    isFactoryGate
                      ? 'inline-flex items-center gap-1 text-sm font-semibold text-[#2EC4B6]'
                      : 'inline-flex items-center gap-1 text-sm font-semibold text-primary'
                  }
                >
                  <ChevronLeft size={18} aria-hidden />
                  返回密码/验证码登录
                </button>
                <QrLoginPanel />
              </div>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div
                className={
                  isFactoryGate
                    ? 'flex gap-1 rounded-md border border-[#3a4450] bg-[#15191f] p-1'
                    : 'flex gap-2 rounded-full bg-[#F0F2F5] p-1'
                }
              >
                {(['password', 'code'] as const).map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setMode(item)}
                    className={
                      isFactoryGate
                        ? `flex-1 rounded-sm py-2.5 text-sm font-medium transition ${
                            mode === item
                              ? 'bg-[#2a323c] text-[#2EC4B6]'
                              : 'text-[#8B94A3] hover:text-[#E8ECF1]'
                          }`
                        : `flex-1 rounded-full py-2.5 text-sm font-medium transition ${
                            mode === item ? 'bg-surface text-primary shadow-sm' : 'text-muted'
                          }`
                    }
                  >
                    {item === 'password' ? '密码登录' : '验证码登录'}
                  </button>
                ))}
              </div>

              <label className="block text-sm">
                <span className={isFactoryGate ? 'font-medium text-[#C5CDD8]' : 'font-medium text-ink'}>手机号</span>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={
                    isFactoryGate
                      ? 'mt-2 w-full rounded-md border border-[#3a4450] bg-[#15191f] px-3 py-2.5 text-[#E8ECF1] outline-none placeholder:text-[#6B7380] focus:border-[#2EC4B6] focus:ring-1 focus:ring-[#2EC4B6]/35'
                      : 'mt-2 w-full rounded-xl border border-border px-3 py-2.5'
                  }
                  placeholder="请输入手机号"
                  autoComplete="tel"
                />
              </label>

              {mode === 'password' ? (
                <label className="block text-sm">
                  <span className={isFactoryGate ? 'font-medium text-[#C5CDD8]' : 'font-medium text-ink'}>密码</span>
                  <div className="relative mt-2">
                    <input
                      type={showLoginPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={
                        isFactoryGate
                          ? 'w-full rounded-md border border-[#3a4450] bg-[#15191f] px-3 py-2.5 pr-10 text-[#E8ECF1] outline-none placeholder:text-[#6B7380] focus:border-[#2EC4B6] focus:ring-1 focus:ring-[#2EC4B6]/35'
                          : 'w-full rounded-xl border border-border px-3 py-2.5 pr-10'
                      }
                      placeholder="请输入密码"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword((value) => !value)}
                      className={`absolute right-3 top-1/2 -translate-y-1/2 ${isFactoryGate ? 'text-[#8B94A3]' : 'text-muted'}`}
                      aria-label={showLoginPassword ? '隐藏密码' : '显示密码'}
                    >
                      {showLoginPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </label>
              ) : (
                <>
                  <label className="block text-sm">
                    <span className={isFactoryGate ? 'font-medium text-[#C5CDD8]' : 'font-medium text-ink'}>验证码</span>
                    <div className="mt-2 flex gap-2">
                      <input
                        value={authCode}
                        onChange={(e) => setAuthCode(e.target.value)}
                        className={
                          isFactoryGate
                            ? 'flex-1 rounded-md border border-[#3a4450] bg-[#15191f] px-3 py-2.5 text-[#E8ECF1] outline-none placeholder:text-[#6B7380] focus:border-[#2EC4B6] focus:ring-1 focus:ring-[#2EC4B6]/35'
                            : 'flex-1 rounded-xl border border-border px-3 py-2.5'
                        }
                        placeholder="6 位验证码"
                        inputMode="numeric"
                      />
                      <button
                        type="button"
                        onClick={handleSendCode}
                        disabled={sendingCode || codeCooldown > 0}
                        className={
                          isFactoryGate
                            ? 'shrink-0 rounded-md border border-[#2EC4B6]/50 px-3 text-sm font-medium text-[#2EC4B6] disabled:opacity-50'
                            : 'shrink-0 rounded-xl border border-primary px-3 text-sm font-medium text-primary disabled:opacity-50'
                        }
                      >
                        {sendingCode ? '验证中…' : codeCooldown > 0 ? `${codeCooldown}s` : '图形验证 · 发送'}
                      </button>
                    </div>
                  </label>
                  <p className={`text-sm leading-5 ${isFactoryGate ? 'text-[#8B94A3]' : 'text-muted'}`}>
                    点击「图形验证 · 发送」后将弹出图形滑块，完成验证后短信验证码自动发出。
                  </p>
                </>
              )}

              {effectiveError ? <FeedbackBanner message={effectiveError} tone="error" /> : null}
              {sendCodeMessage ? <FeedbackBanner message={sendCodeMessage} tone="success" /> : null}

              <button
                type="submit"
                disabled={submitting}
                className={
                  isFactoryGate
                    ? 'w-full rounded-md bg-[#2EC4B6] py-3 text-sm font-semibold text-[#15191f] disabled:opacity-50'
                    : 'w-full rounded-full bg-primary py-3 text-sm font-semibold text-white disabled:opacity-50'
                }
              >
                {submitting ? '请稍候…' : isFactoryGate ? '进入产线' : '立即登录'}
              </button>

              <div className="flex items-center gap-3 py-1">
                <div className={`h-px flex-1 ${isFactoryGate ? 'bg-[#3a4450]' : 'bg-border'}`} />
                <span className={`text-xs ${isFactoryGate ? 'text-[#8B94A3]' : 'text-muted'}`}>或</span>
                <div className={`h-px flex-1 ${isFactoryGate ? 'bg-[#3a4450]' : 'bg-border'}`} />
              </div>

              {wechatLoginIcons}

              {wechatHint ? (
                <p
                  className={
                    isFactoryGate
                      ? 'rounded-md border border-[#3a4450] bg-[#15191f] px-4 py-3 text-center text-sm text-[#C5CDD8]'
                      : 'rounded-xl border border-border bg-[#F7F4EF] px-4 py-3 text-center text-sm text-ink'
                  }
                >
                  {wechatHint}
                </p>
              ) : null}

              {!isFactoryGate ? (
                <div className="flex items-center justify-center gap-3 text-sm">
                  <button type="button" onClick={navigateToSignup} className="font-semibold text-primary">
                    首次注册
                  </button>
                  <span className="text-border">·</span>
                  <button type="button" onClick={navigateToForgotPassword} className="font-semibold text-primary">
                    忘记密码
                  </button>
                </div>
              ) : (
                <p className="text-center text-xs text-[#8B94A3]">
                  开户请先走
                  <Link to="/login" onClick={clearFactoryLoginReturn} className="mx-1 font-medium text-[#2EC4B6]">
                    日记登录
                  </Link>
                  注册；忘记密码同样请回日记入口处理。
                </p>
              )}
            </form>
            )}
          </section>
        ) : null}

        {screen === 'signup' ? (
          <section className="rounded-2xl border border-border bg-surface p-6 shadow-card">
            <div className="mb-5">
              <h2 className="text-base font-semibold text-ink">
                {wechatQrOpen ? '微信小程序扫码注册' : '开户注册'}
              </h2>
              <p className="mt-1 text-sm text-muted">
                {wechatQrOpen
                  ? '打开颗粒日记小程序扫一扫。若尚未开户，确认后会回到本页用手机号完成注册。'
                  : '先完成图形验证，再发送注册短信验证码。新账户只在颗粒日记开通。'}
              </p>
            </div>

            {wechatQrOpen ? (
              <div className="space-y-4">
                <button
                  type="button"
                  onClick={() => {
                    setWechatQrOpen(false);
                    resetFeedback();
                  }}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-primary"
                >
                  <ChevronLeft size={18} aria-hidden />
                  返回手机号注册
                </button>
                <QrLoginPanel />
                <p className="text-center text-sm text-muted">
                  小程序里还没有账户时，请改用微信扫码，然后回到本页填写手机号和验证码。
                </p>
                <div className="flex items-center justify-center gap-3 text-sm">
                  <Link to="/privacy" className="font-semibold text-primary">隐私政策</Link>
                  <span className="text-border">·</span>
                  <button
                    type="button"
                    onClick={() => navigateToSignIn({ phone: normalizePhoneNumber(signupPhone), mode: 'code' })}
                    className="font-semibold text-primary"
                  >
                    返回登录
                  </button>
                </div>
              </div>
            ) : (
            <form onSubmit={handleSignupSubmit} className="space-y-4">
              <label className="block text-sm">
                <span className="font-medium text-ink">手机号</span>
                <input
                  value={signupPhone}
                  onChange={(e) => setSignupPhone(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-border px-3 py-2.5"
                  placeholder="请输入手机号"
                  autoComplete="tel"
                />
                <p className="mt-1 text-xs text-muted">注册成功后将自动登录，支持随时用短信验证码登录，无需提前设置密码。</p>
              </label>

              <label className="block text-sm">
                <span className="font-medium text-ink">短信验证码</span>
                <div className="mt-2 flex gap-2">
                  <input
                    value={signupAuthCode}
                    onChange={(e) => setSignupAuthCode(e.target.value)}
                    className="flex-1 rounded-xl border border-border px-3 py-2.5"
                    placeholder="6 位验证码"
                    inputMode="numeric"
                  />
                  <button
                    type="button"
                    onClick={() => void handleSignupSendCode()}
                    disabled={signupSendingCode || signupCodeCooldown > 0}
                    className="shrink-0 rounded-xl border border-primary px-3 text-sm font-medium text-primary disabled:opacity-50"
                  >
                    {signupSendingCode ? '验证中…' : signupCodeCooldown > 0 ? `${signupCodeCooldown}s` : '图形验证 · 发送'}
                  </button>
                </div>
              </label>

              <p className="text-sm leading-5 text-muted">
                点击「图形验证 · 发送」后将弹出图形滑块，完成验证后短信验证码自动发出。
              </p>

              {effectiveError ? <FeedbackBanner message={effectiveError} tone="error" /> : null}
              {signupMessage ? <FeedbackBanner message={signupMessage} tone="success" /> : null}

              <label className="flex items-start gap-3 text-sm text-muted">
                <button
                  type="button"
                  onClick={() => setAgreedToPolicy((value) => !value)}
                  className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
                    agreedToPolicy ? 'border-primary bg-primary text-white' : 'border-border bg-surface'
                  }`}
                  aria-label="同意隐私政策"
                >
                  {agreedToPolicy ? <Check size={12} strokeWidth={3} /> : null}
                </button>
                <span>
                  我已阅读并同意
                  <Link to="/privacy" className="font-semibold text-primary">《隐私政策》</Link>
                </span>
              </label>

              <button
                type="submit"
                disabled={signupSubmitting}
                className="w-full rounded-full bg-primary py-3 text-sm font-semibold text-white disabled:opacity-50"
              >
                {signupSubmitting ? '注册中…' : '注册并登录'}
              </button>

              <div className="flex items-center gap-3 py-1">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs text-muted">或扫码注册</span>
                <div className="h-px flex-1 bg-border" />
              </div>

              {wechatLoginIcons}

              <p className="text-center text-xs leading-5 text-muted">
                微信扫码后若尚未开户，会回到本站填写手机号和验证码。新账户只在颗粒日记完成。
              </p>

              <div className="flex items-center justify-center gap-3 text-sm">
                <Link to="/privacy" className="font-semibold text-primary">隐私政策</Link>
                <span className="text-border">·</span>
                <button
                  type="button"
                  onClick={() => navigateToSignIn({ phone: normalizePhoneNumber(signupPhone), mode: 'code' })}
                  className="font-semibold text-primary"
                >
                  返回登录
                </button>
              </div>
            </form>
            )}
          </section>
        ) : null}

        {screen === 'forgot-password' ? (
          <section className="rounded-2xl border border-border bg-surface p-6 shadow-card">
            <div className="mb-5">
              <h2 className="text-base font-semibold text-ink">重置密码</h2>
              <p className="mt-1 text-sm text-muted">通过图形验证和短信验证码，设置新的登录密码。</p>
            </div>

            <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
              <label className="block text-sm">
                <span className="font-medium text-ink">手机号</span>
                <input
                  value={forgotPhone}
                  onChange={(e) => setForgotPhone(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-border px-3 py-2.5"
                  placeholder="请输入手机号"
                  autoComplete="tel"
                />
              </label>

              <label className="block text-sm">
                <span className="font-medium text-ink">短信验证码</span>
                <div className="mt-2 flex gap-2">
                  <input
                    value={forgotAuthCode}
                    onChange={(e) => setForgotAuthCode(e.target.value)}
                    className="flex-1 rounded-xl border border-border px-3 py-2.5"
                    placeholder="6 位验证码"
                    inputMode="numeric"
                  />
                  <button
                    type="button"
                    onClick={() => void handleForgotSendCode()}
                    disabled={forgotSendingCode || forgotCodeCooldown > 0}
                    className="shrink-0 rounded-xl border border-primary px-3 text-sm font-medium text-primary disabled:opacity-50"
                  >
                    {forgotSendingCode ? '验证中…' : forgotCodeCooldown > 0 ? `${forgotCodeCooldown}s` : '图形验证 · 发送'}
                  </button>
                </div>
              </label>

              <label className="block text-sm">
                <span className="font-medium text-ink">新密码</span>
                <div className="relative mt-2">
                  <input
                    type={showForgotNewPassword ? 'text' : 'password'}
                    value={forgotNewPassword}
                    onChange={(e) => setForgotNewPassword(e.target.value)}
                    className="w-full rounded-xl border border-border px-3 py-2.5 pr-10"
                    placeholder="至少 8 位，包含字母、数字和符号"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowForgotNewPassword((value) => !value)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted"
                    aria-label={showForgotNewPassword ? '隐藏密码' : '显示密码'}
                  >
                    {showForgotNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {forgotPasswordRuleHint ? <p className="mt-1 text-xs text-danger">{forgotPasswordRuleHint}</p> : null}
              </label>

              <label className="block text-sm">
                <span className="font-medium text-ink">确认新密码</span>
                <div className="relative mt-2">
                  <input
                    type={showForgotConfirmPassword ? 'text' : 'password'}
                    value={forgotConfirmPassword}
                    onChange={(e) => setForgotConfirmPassword(e.target.value)}
                    className="w-full rounded-xl border border-border px-3 py-2.5 pr-10"
                    placeholder="请再次输入新密码"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowForgotConfirmPassword((value) => !value)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted"
                    aria-label={showForgotConfirmPassword ? '隐藏密码' : '显示密码'}
                  >
                    {showForgotConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {forgotPasswordMismatchHint ? <p className="mt-1 text-xs text-danger">{forgotPasswordMismatchHint}</p> : null}
              </label>

              <p className="text-sm leading-5 text-muted">
                点击「图形验证 · 发送」后将弹出图形滑块，完成验证后短信验证码自动发出。
              </p>

              {effectiveError ? <FeedbackBanner message={effectiveError} tone="error" /> : null}
              {forgotMessage ? <FeedbackBanner message={forgotMessage} tone="success" /> : null}

              <button
                type="submit"
                disabled={forgotSubmitting}
                className="w-full rounded-full bg-primary py-3 text-sm font-semibold text-white disabled:opacity-50"
              >
                {forgotSubmitting ? '更新中…' : '更新密码'}
              </button>

              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => navigateToSignIn({ phone: normalizePhoneNumber(forgotPhone), mode: 'password' })}
                  className="text-sm font-semibold text-primary"
                >
                  返回登录
                </button>
              </div>
            </form>
          </section>
        ) : null}

        {screen === 'signin' ? (
          <p className={`mt-6 text-center text-xs ${isFactoryGate ? 'text-[#7A8492]' : 'text-muted'}`}>
            登录即表示同意
            <Link to="/privacy" className={isFactoryGate ? 'text-[#2EC4B6]' : 'text-primary'}> 隐私政策 </Link>
            、
            <Link to="/terms" className={isFactoryGate ? 'text-[#2EC4B6]' : 'text-primary'}> 用户协议 </Link>
            与
            <Link to="/copyright" className={isFactoryGate ? 'text-[#2EC4B6]' : 'text-primary'}> 版权声明</Link>
            {isFactoryGate ? null : (
              <>
                {' · '}
                <Link to="/download?view=1" className="text-primary">下载 App</Link>
              </>
            )}
          </p>
        ) : null}
      </div>

      <AliyunCaptchaModal
        visible={captchaVisible}
        onClose={() => {
          setCaptchaVisible(false);
          setPendingCaptchaRequest(null);
        }}
        onError={(message) => setLocalError(message)}
        onSuccess={(result) => void handleCaptchaSuccess(result)}
      />
    </div>
  );
}
